import { useState } from "react";
import { createPortal } from "react-dom";
import { Avatar, Button, Table, Tag, Tooltip } from "antd";
import CustomerCareDrawer from "./CustomerCareDrawer";
import useTableColumnSettings, { SettingColumn } from "../../hooks/useTableColumnSettings";
import { ColumnSettingButton } from "../../components/TableColumnSetting";

import {
    Crown,
    MessageCircle,
    Phone,
    Star,
} from "lucide-react";

interface Props {
    data: any[];
    loading?: boolean;
    pagination?: {
        current: number;
        pageSize: number;
        total: number;
        onChange: (page: number, pageSize: number) => void;
    };
    onRefresh?: () => void;
    /** Chỗ trên toolbar của trang để đặt nút cài đặt cột; không truyền thì đặt ngay trên bảng */
    columnSettingSlot?: HTMLElement | null;
}

export default function CustomerTable({ data, loading, pagination, onRefresh, columnSettingSlot }: Props) {
    const [openDrawer, setOpenDrawer] = useState(false);
    const [customer, setCustomer] = useState<any>(null);

    const handleOpen = (record: any) => {
        setCustomer(record);
        setOpenDrawer(true);
    };

    const columns: SettingColumn<any>[] = [
        {
            title: "Khách hàng",
            dataIndex: "name",
            width: 260,
            render: (_, record) => (
                <div className="flex items-center gap-3">
                    <Avatar
                        size={44}
                        style={{
                            background: record.color,
                            color: "#2563eb",
                            fontWeight: 700,
                        }}
                    >
                        {record.avatar}
                    </Avatar>

                    <div>
                        <div className="flex items-center gap-1 font-semibold text-[15px] dark:text-white">
                            {record.name}

                            {record.csat !== null && record.csat >= 4.5 && (
                                <Tooltip title={`CSAT ${record.csat} sao`}>
                                    <Crown
                                        size={15}
                                        className="fill-amber-400 text-amber-400"
                                    />
                                </Tooltip>
                            )}
                        </div>

                        <div className="text-xs text-slate-400">
                            {record.code}
                        </div>
                    </div>
                </div>
            ),
        },

        {
            title: "Liên hệ",
            dataIndex: "phone",
            width: 150,
        },

        {
            title: "Dịch vụ đã làm",
            dataIndex: "service",
            width: 240,
            render: (value, record) => (
                <div>
                    <Tooltip title={value}>
                        <div className="font-medium dark:text-white line-clamp-2">
                            {value}
                        </div>
                    </Tooltip>

                    {record.doctor && (
                        <div className="text-xs text-slate-400 mt-1">
                            {record.doctor}
                        </div>
                    )}
                </div>
            ),
        },

        {
            title: "Ngày làm",
            dataIndex: "date",
            width: 150,
        },

        {
            title: "Trạng thái chăm sóc",
            dataIndex: "status",
            width: 200,
            render: (_, record) => {
                const cared = !!record.is_cared;
                const colors = cared
                    ? { bg: "#DCFCE7", text: "#16A34A", dot: "#22C55E" }
                    : { bg: "#FEF3C7", text: "#D97706", dot: "#F59E0B" };

                return (
                    <Tooltip
                        title={
                            cared && (record.cared_by_staff_name || record.cared_at)
                                ? `${record.cared_by_staff_name || ""}${record.cared_at ? ` — ${record.cared_at}` : ""}`
                                : undefined
                        }
                    >
                        <Tag
                            bordered={false}
                            className="rounded-full px-3 py-[4px]"
                            style={{ background: colors.bg, color: colors.text }}
                        >
                            <span className="flex items-center gap-2">
                                <span
                                    className="w-2 h-2 rounded-full"
                                    style={{ background: colors.dot }}
                                />
                                {record.status}
                            </span>
                        </Tag>
                    </Tooltip>
                );
            },
        },

        {
            title: "CSAT",
            dataIndex: "csat",
            width: 110,
            align: "center",
            render: (value) =>
                value !== null && value !== undefined ? (
                    <div className="flex items-center justify-center gap-1 text-amber-500">
                        <Star
                            size={15}
                            className="fill-amber-500"
                        />

                        {value}
                    </div>
                ) : (
                    <span className="text-slate-400">
                        Chưa ĐG
                    </span>
                ),
        },

        {
            key: "actions",
            title: "Thao tác",
            width: 110,
            align: "center",
            fixed: "right",
            lockVisible: true,
            render: (_, record) => (
                <div className="flex justify-center gap-2">

                    <Button
                        shape="circle"
                        icon={<Phone size={16} />}
                        onClick={() => handleOpen(record)}
                    />

                    <Button
                        shape="circle"
                        icon={<MessageCircle size={16} />}
                        onClick={() => handleOpen(record)}
                    />

                </div>
            )
        }
    ];

    // Ẩn/hiện, sắp xếp, cố định, độ rộng cột — lưu theo tài khoản (extra_data)
    const {
        columns: tableColumns,
        components: tableComponents,
        tableLayout,
        scrollX,
        settingProps: columnSettingProps,
    } = useTableColumnSettings("care_list", columns, { scrollX: 1100 });

    const tablePagination = pagination
        ? {
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            onChange: pagination.onChange,
            showTotal: (t: number, range: [number, number]) => `${range[0]}-${range[1]} / ${t}`,
        }
        : { pageSize: 10, showSizeChanger: true };

    return (
        <>
            {/* Desktop */}

            <div className="hidden lg:block">

                {columnSettingSlot
                    ? createPortal(<ColumnSettingButton {...columnSettingProps} />, columnSettingSlot)
                    : (
                        <div className="flex justify-end mb-2">
                            <ColumnSettingButton {...columnSettingProps} />
                        </div>
                    )}

                <Table
                    rowKey="key"
                    columns={tableColumns}
                    components={tableComponents}
                    tableLayout={tableLayout}
                    dataSource={data}
                    loading={loading}
                    pagination={tablePagination}
                    scroll={{ x: scrollX }}
                    className="rounded-xl overflow-hidden"
                />

            </div>

            {/* Mobile */}

            <div className="space-y-4 lg:hidden">

                {data.map((item) => (
                    <div
                        key={item.key}
                        className="rounded-2xl border bg-white dark:bg-[#262626] dark:border-neutral-700 p-4 shadow-sm"
                    >
                        <div className="flex gap-3">

                            <Avatar
                                size={46}
                                style={{
                                    background: item.color,
                                    color: "#2563eb",
                                }}
                            >
                                {item.avatar}
                            </Avatar>

                            <div className="flex-1">

                                <div className="font-semibold dark:text-white flex items-center gap-1">
                                    {item.name}

                                    {item.csat !== null && item.csat >= 4.5 && (
                                        <Crown
                                            size={14}
                                            className="fill-amber-400 text-amber-400"
                                        />
                                    )}
                                </div>

                                <div className="text-xs text-slate-400">
                                    {item.code}
                                </div>

                                <div className="mt-3 text-sm">
                                    {item.phone}
                                </div>

                                <div className="mt-2 font-medium dark:text-white">
                                    {item.service}
                                </div>

                                {item.doctor && (
                                    <div className="text-xs text-slate-400">
                                        {item.doctor}
                                    </div>
                                )}

                                <div className="text-xs text-slate-400">
                                    {item.date}
                                </div>

                                <div className="mt-3">
                                    <Tag
                                        bordered={false}
                                        color={item.is_cared ? "success" : "warning"}
                                        className="rounded-full"
                                    >
                                        {item.status}
                                    </Tag>
                                </div>

                                <div className="flex justify-between items-center mt-4">

                                    <div className="flex items-center gap-1 text-amber-500">
                                        {item.csat !== null && item.csat !== undefined ? (
                                            <>
                                                <Star
                                                    size={15}
                                                    className="fill-amber-500"
                                                />
                                                {item.csat}
                                            </>
                                        ) : (
                                            <span className="text-slate-400">
                                                Chưa ĐG
                                            </span>
                                        )}
                                    </div>

                                    <div className="flex gap-2">

                                        <Button
                                            shape="circle"
                                            icon={<Phone size={15} />}
                                            onClick={() => handleOpen(item)}
                                        />

                                        <Button
                                            shape="circle"
                                            icon={<MessageCircle size={15} />}
                                            onClick={() => handleOpen(item)}
                                        />

                                    </div>

                                </div>

                            </div>

                        </div>
                    </div>
                ))}

            </div>

            <CustomerCareDrawer
                open={openDrawer}
                customer={customer}
                onClose={() => setOpenDrawer(false)}
                onUpdated={onRefresh}
            />
        </>
    );
}
